<template>
  <fieldset>
    <legend>{{ $t('registrationForm.address') }}</legend>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }" for="address-postalCode">{{ $t('registrationForm.postalCode') }}</label>
        <text-input
          :value="address.postalCode"
          :errors="postalCodeValidationErrors"
          :maxlength="6"
          :prefix="'address'"
          :placeholder="postalCodePlaceholder"
          name="postalCode"
          @input="(v) => updateAddressProperty('postalCode', upper(v))"/>
        <registration-form-validation-messages :messages="postalCodeValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }" for="address-houseNumber">
          {{ $t('registrationForm.houseNumberAndExtension') }}
        </label>
        <text-input
          :value="address.houseNumber"
          :errors="houseNumberValidationErrors"
          :maxlength="5"
          :prefix="'address'"
          name="houseNumber"
          @input="(v) => updateAddressProperty('houseNumber', v)"/>
        <text-input
          :value="address.houseNumberExtension"
          :errors="houseNumberExtensionValidationErrors"
          :maxlength="10"
          :prefix="'address'"
          name="houseNumberExtension"
          @input="(v) => updateAddressProperty('houseNumberExtension', v)"/>
        <registration-form-validation-messages :messages="houseNumberValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label for="address-street" class="optional">{{ $t('registrationForm.street') }}</label>

        <text-input
          :value="address.street"
          :errors="streetValidationErrors"
          :maxlength="50"
          :prefix="'address'"
          name="street"
          @focus="refreshAddress"
          @input="(v) => updateAddressProperty('street', v)"/>

        <registration-form-validation-messages :messages="streetValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label for="address-city" class="optional">{{ $t('registrationForm.city') }}</label>
        <text-input
          :value="address.city"
          :errors="cityValidationErrors"
          :maxlength="50"
          :prefix="'address'"
          name="city"
          @input="(v) => updateAddressProperty('city', v)"/>

        <registration-form-validation-messages :messages="cityValidationErrors"/>
      </div>
    </div>
    <div :class="{ 'form-row': true, 'inline-label': true, required: !storeUser }">
      <div class="form-column wm-field-label">
        <label :class="{ required: !storeUser }" for="address-countryCode">{{ $t('registrationForm.country') }}</label>

        <select-input
          :value="address.countryCode || ''"
          :options="countryOptions"
          name="countryCode"
          @input="(v) => updateAddressProperty('countryCode', v)"/>
        <registration-form-validation-messages :messages="countryCodeValidationErrors"/>
      </div>
    </div>
  </fieldset>
</template>

<script>
import Vue from 'vue'
import IbanField from '../components/IbanField'
import debounce from 'lodash.debounce'
import uniqBy from 'lodash.uniqby'
import memoizeOne from 'memoize-one'
import { mapState } from 'vuex'

import TextInput from './registration-form/TextInput'
import SelectInput from './registration-form/SelectInput'
import RegistrationFormValidationMessages from './registration-form/ValidationMessages'
import {
  CLEAR_USER_VALIDATION_ERROR_BY_PROPERTY_PATH,
  SET_USER_PROFILE_ADDRESS_FIELD
} from '../store/modules/user/mutation-types'
import {
  CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH,
  SET_INVOICE_ADDRESS_FIELD
} from '../store/modules/cart/mutation-types'
import { upper } from 'change-case'
import { COUNTRY_CODE_NLD, COUNTRY_CODE_BEL, COUNTRY_CODE_DEU } from '../types/country-code'

const uniqByCode = arr => uniqBy(arr, (a) => a.code)
const mapValidationErrors = (propertyNames) => {
  return Object.assign({}, ...propertyNames.map(propertyName => (
    {
      [propertyName + 'ValidationErrors']: function () {
        return uniqByCode([
          ...this.orderValidationErrors.filter(error => error.propertyPath === 'order.invoiceAddress.' + propertyName),
          ...this.userValidationErrors.filter(error => error.propertyPath === 'order.profile.address.' + propertyName)
        ])
      }
    }
  )))
}

export default {
  name: 'RegistrationForm',
  components: {
    RegistrationFormValidationMessages,
    IbanField,
    TextInput,
    SelectInput
  },
  props: {
    address: {
      type: Object,
      required: true
    },
    storeUser: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      countryOptions: [
        { value: COUNTRY_CODE_NLD, text: this.$t('registrationForm.nederland') },
        { value: COUNTRY_CODE_BEL, text: this.$t('registrationForm.belgie') },
        { value: COUNTRY_CODE_DEU, text: this.$t('registrationForm.duitsland') }
      ]
    }
  },
  computed: {
    ...mapState({
      orderValidationErrors: state => state.cart.orderValidationErrors,
      userProfile: state => (state.user && state.user.user && state.user.user.profile ? state.user.user.profile : null),
      userValidationErrors: state => state.user.userValidationErrors
    }),
    ...mapValidationErrors(['street', 'houseNumber', 'houseNumberExtension', 'postalCode', 'city', 'countryCode']),
    postalCodePlaceholder: function () {
      const placeHolders = {
        [COUNTRY_CODE_NLD]: '9999AA',
        [COUNTRY_CODE_BEL]: '1234',
        [COUNTRY_CODE_DEU]: '12345'
      }

      return placeHolders[this.address.countryCode] || placeHolders[COUNTRY_CODE_NLD]
    }
  },
  methods: {
    upper (value) {
      return upper(value)
    },
    updateAddressProperty (name, value) {
      this.clearFieldValidationMessagesIfExists(name)
      this.$store.commit('cart/' + SET_INVOICE_ADDRESS_FIELD, { name, value })
      if (this.userProfile) {
        this.$store.commit('user/' + SET_USER_PROFILE_ADDRESS_FIELD, { name, value })
      }
    },
    fetchAndApplyAddressInformation: memoizeOne(async function (postalCode, houseNumber, houseNumberExtension) {
      const data = await Vue.$container.orderApiClient.getAddressInformation(postalCode, houseNumber, houseNumberExtension)
      const foundAddress = data.address
      if (foundAddress.street && foundAddress.city) {
        let doUpdate = true
        if ((this.address.street && this.address.street !== foundAddress.street) ||
          (this.address.city && this.address.city !== foundAddress.city)) {
          doUpdate = confirm(this.$t('registrationForm.postalCode_update_address_info', {
            street: foundAddress.street,
            city: foundAddress.city
          }))
        }
        if (doUpdate) {
          this.updateAddressProperty('street', foundAddress.street)
          this.updateAddressProperty('city', foundAddress.city)
        }
      }
    }),
    refreshAddress: debounce(async function () {
      if (this.address.postalCode !== '' &&
        this.address.postalCode !== null &&
        this.address.houseNumber !== null
      ) {
        try {
          await this.fetchAndApplyAddressInformation(this.address.postalCode, this.address.houseNumber, this.address.houseNumberExtension)
        } catch (e) { // not finding an address is not an exceptional case that we must handle, just ignore it
          Vue.$container.log.debug(e)
        }
      }
    }, 100),
    clearFieldValidationMessagesIfExists (propertyName) {
      const fieldValidationMessages = this[propertyName + 'ValidationErrors']
      if (fieldValidationMessages && fieldValidationMessages.length > 0) {
        this.$store.commit('cart/' + CLEAR_ORDER_VALIDATION_ERROR_BY_PROPERTY_PATH, 'order.invoiceAddress.' + propertyName)
        this.$store.commit('user/' + CLEAR_USER_VALIDATION_ERROR_BY_PROPERTY_PATH, 'order.profile.address' + propertyName)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/forms/CustomerAddressForm.vue